import { EditableStageView } from "@softmax/product-ux/editor";
import type { ReactNode } from "react";

import type { HeroMediaItem } from "../store-page/types";
import { GeneralsBoard } from "@/features/preview/board";
import type { PreviewMediaItem } from "@/features/preview/records";
import { useEditModeMaybe } from "../use-edit-mode-maybe";
import { IconField } from "./icon-field";
import { MediaTray } from "./media-tray";

function heroMediaToPreviewItem(item: HeroMediaItem): PreviewMediaItem {
  if (item.kind === "video_url") {
    return {
      kind: "highlight",
      src: item.src,
      alt: item.alt,
      poster: item.poster ?? null,
    };
  }

  return { kind: "asset", src: item.src, alt: item.alt };
}

export function EditableStage({
  liveMedia,
  name,
  uppercase,
  replayEmbed,
  landscape,
}: {
  readonly liveMedia: readonly PreviewMediaItem[];
  name: string;
  uppercase: boolean;
  replayEmbed?: ReactNode;
  landscape?: boolean | undefined;
}) {
  const mode = useEditModeMaybe();
  const editing = mode?.editing ?? false;
  let items = liveMedia;

  if (mode && editing) {
    const draftItems = mode.editor.content.hero_media
      .toSorted((a, b) => a.order - b.order)
      .map(heroMediaToPreviewItem);
    // Only the published stage can include a recorded replay embed.
    items = draftItems.length
      ? draftItems
      : liveMedia.filter((item) => item.kind !== "replay");
  }

  return (
    <EditableStageView
      items={items}
      name={name}
      uppercase={uppercase}
      landscape={landscape}
      editing={editing}
      board={
        items.some((item) => item.kind === "board") ? (
          <GeneralsBoard size={400} />
        ) : undefined
      }
      replayEmbed={editing ? undefined : replayEmbed}
      mediaTray={
        editing ? (
          <>
            <IconField />
            <MediaTray />
          </>
        ) : undefined
      }
    />
  );
}
